/**
 * Admin Control Panel Page
 * 
 * Phase 1: User Analytics Dashboard
 * Phase 2: Conversion Funnel & Marketing Effectiveness
 * Provides summary cards with drill-down modals for user data analysis.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { 
  Users, UserPlus, Activity, UserX, 
  RefreshCw, Loader2, TrendingUp, TrendingDown, Minus,
  GitBranch
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import AdminNav from "../components/AdminNav";
import AdminTopMenuBar from "../components/AdminTopMenuBar";
import AdminMobileBottomNav from "../components/AdminMobileBottomNav";
import UniversalFooter from "../components/UniversalFooter";
import DateFilter from "../components/admin/control-panel/DateFilter";
import SummaryCard from "../components/admin/control-panel/SummaryCard";
import DrillDownModal from "../components/admin/control-panel/DrillDownModal";
import ConversionFunnel from "../components/admin/control-panel/ConversionFunnel";
import MarketingEffectivenessSection from "../components/admin/control-panel/MarketingEffectivenessSection";
import ConversionDrillDownModal from "../components/admin/control-panel/ConversionDrillDownModal";
import { API_URL } from '@/config/api';

const AdminControlPanelPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    // Default: Today
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    return {
      start: todayStr,
      end: todayStr
    };
  });
  
  // Modal state for Phase 1 User Analytics
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null); // 'all' | 'new' | 'active' | 'inactive'
  const [modalTitle, setModalTitle] = useState('');

  // Phase 2: Conversion funnel state
  const [conversionData, setConversionData] = useState(null);
  const [conversionLoading, setConversionLoading] = useState(true);
  const [conversionError, setConversionError] = useState(null);
  
  // Modal state for Phase 2 Conversion Funnel
  const [conversionModalOpen, setConversionModalOpen] = useState(false);
  const [conversionModalStage, setConversionModalStage] = useState(null);
  const [conversionModalTitle, setConversionModalTitle] = useState('');

  const fetchSummaryData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/analytics/users/summary?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch analytics');
      }
      
      const data = await response.json();
      setSummaryData(data);
    } catch (err) {
      console.error('Error fetching user analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // Phase 2: Fetch conversion funnel data
  const fetchConversionData = useCallback(async () => {
    setConversionLoading(true);
    setConversionError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/analytics/conversion/summary?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch conversion data');
      }
      
      const data = await response.json();
      setConversionData(data);
    } catch (err) {
      console.error('Error fetching conversion data:', err);
      setConversionError(err.message);
    } finally {
      setConversionLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchConversionData();
  }, [fetchConversionData]);

  const handleCardClick = (type, title) => {
    setModalType(type);
    setModalTitle(title);
    setModalOpen(true);
  };

  // Phase 2: Handle funnel stage click
  const handleFunnelStageClick = (stage, title) => {
    setConversionModalStage(stage);
    setConversionModalTitle(title);
    setConversionModalOpen(true);
  };

  // Phase 2: Handle effectiveness card click (for "Never Listened")
  const handleEffectivenessCardClick = (key, title) => {
    if (key === 'never_listened') {
      setConversionModalStage('never_listened');
      setConversionModalTitle('Registered, Never Listened');
      setConversionModalOpen(true);
    }
  };

  const calculateTrend = (current, previous) => {
    if (previous === 0) {
      return current > 0 ? { direction: 'up', value: 100 } : { direction: 'neutral', value: 0 };
    }
    const percentChange = ((current - previous) / previous) * 100;
    if (percentChange > 0) {
      return { direction: 'up', value: percentChange.toFixed(1) };
    } else if (percentChange < 0) {
      return { direction: 'down', value: Math.abs(percentChange).toFixed(1) };
    }
    return { direction: 'neutral', value: 0 };
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-emerald-400" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-400" />;
      default:
        return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-control-panel">
      <AdminNav />
      <AdminTopMenuBar />
      
      <main className="mx-auto max-w-7xl px-4 py-8 pb-24 md:pb-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Control Panel</h1>
          <p className="text-slate-400 mt-1">Platform analytics and user insights</p>
        </div>

        {/* Date Filter */}
        <DateFilter 
          dateRange={dateRange} 
          onDateRangeChange={setDateRange}
          onRefresh={fetchSummaryData}
          loading={loading}
        />

        {/* User Analytics Section */}
        <section className="mt-8" data-testid="user-analytics-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-900/50">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">User Analytics</h2>
                <p className="text-sm text-slate-400">
                  {dateRange.start} to {dateRange.end}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchSummaryData}
              disabled={loading}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="refresh-analytics-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Loading State */}
          {loading && !summaryData && (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <Card className="bg-red-900/20 border-red-700/50">
              <CardContent className="py-8 text-center">
                <p className="text-red-400">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchSummaryData}
                  className="mt-4 border-red-700 text-red-400 hover:bg-red-900/30"
                >
                  Retry
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Summary Cards */}
          {summaryData && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Registered Users */}
              <SummaryCard
                title="Total Registered"
                value={summaryData.total_registered_users}
                icon={Users}
                iconBgColor="bg-slate-800"
                iconColor="text-slate-300"
                description="All-time users"
                onClick={() => handleCardClick('all', 'All Registered Users')}
                testId="card-total-registered"
              />

              {/* New Registrations */}
              <SummaryCard
                title="New Registrations"
                value={summaryData.new_registrations}
                icon={UserPlus}
                iconBgColor="bg-emerald-900/50"
                iconColor="text-emerald-400"
                trend={calculateTrend(summaryData.new_registrations, summaryData.new_registrations_prev_period)}
                trendLabel="vs previous period"
                onClick={() => handleCardClick('new', 'New Registrations')}
                testId="card-new-registrations"
              />

              {/* Active Users */}
              <SummaryCard
                title="Active Users"
                value={summaryData.active_users}
                icon={Activity}
                iconBgColor="bg-blue-900/50"
                iconColor="text-blue-400"
                trend={calculateTrend(summaryData.active_users, summaryData.active_users_prev_period)}
                trendLabel="vs previous period"
                onClick={() => handleCardClick('active', 'Active Users')}
                testId="card-active-users"
              />

              {/* Inactive Users */}
              <SummaryCard
                title="Inactive Users"
                value={summaryData.inactive_users}
                icon={UserX}
                iconBgColor="bg-amber-900/50"
                iconColor="text-amber-400"
                description="No activity in period"
                onClick={() => handleCardClick('inactive', 'Inactive Users')}
                testId="card-inactive-users"
              />
            </div>
          )}
        </section>

        {/* Phase 2: Conversion Funnel Section */}
        <section className="mt-10" data-testid="conversion-funnel-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-r from-blue-900/50 to-emerald-900/50">
                <GitBranch className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Conversion Funnel</h2>
                <p className="text-sm text-slate-400">
                  User journey from visitor to engaged listener
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchConversionData}
              disabled={conversionLoading}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="refresh-conversion-btn"
            >
              <RefreshCw className={`w-4 h-4 ${conversionLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Conversion Error State */}
          {conversionError && (
            <Card className="bg-red-900/20 border-red-700/50 mb-6">
              <CardContent className="py-6 text-center">
                <p className="text-red-400">{conversionError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchConversionData}
                  className="mt-4 border-red-700 text-red-400 hover:bg-red-900/30"
                >
                  Retry
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Conversion Funnel Visualization */}
          <ConversionFunnel
            data={conversionData}
            onStageClick={handleFunnelStageClick}
            loading={conversionLoading}
          />
        </section>

        {/* Phase 2: Marketing Effectiveness Section */}
        <section className="mt-10" data-testid="marketing-effectiveness-section">
          <MarketingEffectivenessSection
            data={conversionData}
            onCardClick={handleEffectivenessCardClick}
            loading={conversionLoading}
          />
        </section>

        {/* Future: Content Analytics Section */}
        {/* Future: Revenue Analytics Section */}
        {/* Future: System Analytics Section */}
      </main>

      {/* Phase 1: User Analytics Drill-Down Modal */}
      <DrillDownModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={modalTitle}
        filterType={modalType}
        dateRange={dateRange}
      />

      {/* Phase 2: Conversion Funnel Drill-Down Modal */}
      <ConversionDrillDownModal
        open={conversionModalOpen}
        onOpenChange={setConversionModalOpen}
        title={conversionModalTitle}
        stage={conversionModalStage}
        dateRange={dateRange}
      />

      <AdminMobileBottomNav />
      <UniversalFooter />
    </div>
  );
};

export default AdminControlPanelPage;
